import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthContext'
import { apiGet } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import type { Project, TeamMember } from '../../lib/types'
import { IconoCerrar } from '../common'
import { agregarAlProyecto, puedeEditarMiembros } from './miembros'
import './avisoFuera.css'

// El aviso de "arrobaste a alguien que no está en el proyecto" (pedido de Damián).
//
// NO bloquea nada: el mensaje se publica igual, con o sin aviso. Solo dice lo que
// pasó —la mención a un no-miembro no notifica, el API la filtra para no sacar
// contenido del proyecto a quien no puede abrirlo— y ofrece arreglarlo ahí mismo.
//
// Quién puede arreglarlo es lo MISMO que exige PUT /members (puedeEditarMiembros):
// owner o admin, de casa y con PROYECTOS.MIEMBROS.EDITAR. A quien no puede se le
// dice a quién pedírselo en vez de pintarle un botón que rebotaría con 403.
//
// El padre decide QUÉ correos avisar y guarda los descartados (cada pantalla tiene
// su propio ciclo: el composer se remonta al enviar, el editor de mensajes se
// desmonta al publicar). Este componente solo se encarga de filtrar a quien ya es
// miembro —en cuanto el alta se guarda, el proyecto en caché cambia y la fila se va
// sola— y de confirmar lo que agregó.

/**
 * 'antes': todavía se está escribiendo; agregarlo ahora hace que la mención SÍ le
 * llegue al publicar. 'despues': ya se publicó; la mención no le llegó, pero al
 * agregarlo recibe el aviso de "Te agregaron a un proyecto".
 */
export type MomentoAviso = 'antes' | 'despues'

export function AvisoFueraDelProyecto({
  project,
  emails,
  momento,
  onDescartar,
}: {
  project: Project
  emails: string[]
  momento: MomentoAviso
  onDescartar: (email: string) => void
}) {
  const { me, can } = useAuth()
  const qc = useQueryClient()
  // Misma llave que el resto de la app: el roster ya está en caché casi siempre.
  const { data: team = [] } = useQuery({
    queryKey: qk.team,
    queryFn: () => apiGet<{ members: TeamMember[] }>('/api/team'),
    select: (d) => d.members,
  })
  const [enVuelo, setEnVuelo] = useState<string[]>([])
  const [agregados, setAgregados] = useState<string[]>([])
  const [fallos, setFallos] = useState<Record<string, string>>({})

  const nombreDe = (email: string) =>
    team.find((t) => t.email.toLowerCase() === email)?.name || email.split('@')[0] || email
  const puede = puedeEditarMiembros(me, project, can)

  const miembros = new Set((project.memberEmailsResolved ?? []).map((e) => e.toLowerCase()))
  const pendientes = [...new Set(emails.map((e) => e.toLowerCase()))].filter(
    (e) => !miembros.has(e) && !agregados.includes(e),
  )
  // Sin nada que decir NO se devuelve null: la región aria-live tiene que existir
  // ANTES de que le llegue el texto, o el lector de pantalla no anuncia nada (el
  // aviso aparece justo cuando la región se monta). Vacía no ocupa lugar (.css).

  const agregar = async (email: string) => {
    setEnVuelo((prev) => [...prev, email])
    setFallos((prev) => {
      const resto = { ...prev }
      delete resto[email]
      return resto
    })
    try {
      const res = await agregarAlProyecto(project.id, email)
      // La respuesta del PUT trae el proyecto fresco y su etag: se escribe directo
      // en la caché de la página del proyecto (que es de donde sale el `project` de
      // todas las pestañas) para que la persona pase a miembro en el acto.
      if (res.etag) qc.setQueryData(qk.project(project.id), { project: res.project, etag: res.etag })
      else void qc.invalidateQueries({ queryKey: qk.project(project.id) })
      void qc.invalidateQueries({ queryKey: qk.activity(project.id) })
      // El dashboard trae el conteo de miembros.
      void qc.invalidateQueries({ queryKey: ['projects'] })
      setAgregados((prev) => (prev.includes(email) ? prev : [...prev, email]))
    } catch (err) {
      setFallos((prev) => ({
        ...prev,
        [email]: `No se pudo agregar a ${nombreDe(email)}: ${err instanceof Error ? err.message : 'error desconocido'}`,
      }))
    } finally {
      setEnVuelo((prev) => prev.filter((e) => e !== email))
    }
  }

  // A quién pedírselo. Si quien escribe ES el owner y aun así no puede (le quitaron
  // la clave, o es invitado), pedírselo "a sí mismo" no tendría sentido.
  // Sin owner legible (un doc viejo o a medio cargar) también se cae a "un admin":
  // el componente ya no se desmonta cuando no hay nada que avisar y no puede tronar.
  const owner = (project.ownerEmail ?? '').toLowerCase()
  const aQuienPedir = !owner || me?.email.toLowerCase() === owner ? 'un admin' : nombreDe(owner)

  return (
    // aria-live y no role="alert": es información, no un error, y no debe
    // interrumpir a quien sigue escribiendo.
    <div className="aviso-fuera" aria-live="polite">
      {pendientes.map((email) => {
        const nombre = nombreDe(email)
        const ocupado = enVuelo.includes(email)
        return (
          <div key={email} className="aviso-fuera-fila">
            <span className="aviso-fuera-texto">
              <strong>{nombre}</strong> no está en el proyecto,{' '}
              {momento === 'antes' ? 'así que no le llegará la mención.' : 'así que no le llegó la mención.'}
              {!puede && ` Pídele a ${aQuienPedir} que agregue a ${nombre}.`}
              {fallos[email] && <span className="aviso-fuera-fallo"> {fallos[email]}</span>}
            </span>
            {puede && (
              <button
                type="button"
                className="btn btn-ghost aviso-fuera-accion"
                onClick={() => void agregar(email)}
                disabled={ocupado}
                aria-busy={ocupado || undefined}
                aria-label={ocupado ? `Agregando a ${nombre}…` : `Agregar a ${nombre} al proyecto`}
              >
                {ocupado ? 'Agregando…' : 'Agregar al proyecto'}
              </button>
            )}
            <button
              type="button"
              className="icon-btn aviso-fuera-cerrar"
              onClick={() => onDescartar(email)}
              aria-label={`Descartar el aviso de ${nombre}`}
              title="Descartar"
            >
              <IconoCerrar />
            </button>
          </div>
        )
      })}
      {agregados.map((email) => (
        <div key={email} className="aviso-fuera-fila aviso-fuera-listo">
          <span className="aviso-fuera-texto">
            Listo: <strong>{nombreDe(email)}</strong> ya está en el proyecto
            {momento === 'antes' ? ' y le llegará la mención al publicar.' : ' y recibió el aviso.'}
          </span>
          <button
            type="button"
            className="icon-btn aviso-fuera-cerrar"
            onClick={() => setAgregados((prev) => prev.filter((e) => e !== email))}
            aria-label="Cerrar confirmación"
            title="Cerrar"
          >
            <IconoCerrar />
          </button>
        </div>
      ))}
    </div>
  )
}
